// src/components/Pages/VideoProducerLabPage.js

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Container, Title, Text, Paper, Group, Button, Stack, Alert,
    Code, Badge, Tabs, Divider, ThemeIcon, useMantineTheme,
    useMantineColorScheme, Grid, Tooltip, Anchor,
    Center, TextInput, Loader, Switch, ActionIcon, Collapse,
    Slider, SegmentedControl, List, Progress,
    SimpleGrid, Card, Image, Stepper, Checkbox
} from '@mantine/core';
import {
    IconVideo, IconDatabase, IconSearch, IconAlertCircle, IconCheck,
    IconMusic, IconPlayerPlay, IconScript, IconCode, IconExternalLink,
    IconBrandYoutube, IconWand, IconCurrencyDollar, IconMicrophone,
    IconMicrophoneOff, IconCopy, IconInfoCircle, IconCloudUpload, IconClock,
    IconPlayerSkipForward, IconX
} from '@tabler/icons-react';


import { notifications } from '@mantine/notifications';
import { useAuth } from '../../context/AuthContext';
import { Player } from "@remotion/player";

// --- TEMPLATE COMPOSITIONS (Aliased for Multi-Format) ---
import { Composition as MSN_Landscape_Composition } from "../VideoEngine/Templates/MSN_Landscape/Composition";
import { Composition as MSN_Portrait_Composition } from "../VideoEngine/Templates/MSN_Portrait/Composition";
import { Composition as LoveInc_Landscape_Composition } from "../VideoEngine/Templates/LoveInc_Landscape/Composition";
import { Composition as TikTok_Portrait_Composition } from "../VideoEngine/Templates/TikTok_Portrait/Composition";

import { AudioPreview } from "../VideoEngine/Common/AudioPreview";

// --- FIREBASE IMPORTS (Cleaned for Fire & Forget) ---
import { getFirestore, collection, addDoc, serverTimestamp, doc } from 'firebase/firestore';
// getStorage, ref, and getDownloadURL have been removed. updateDoc and getDoc removed.

// --- FORMAT DEFINITIONS ---
const FORMATS = [
    { id: 'MSN_Landscape', label: 'MSN Landscape', desc: '16:9 General', comp: MSN_Landscape_Composition, width: 1920, height: 1080 },
    { id: 'MSN_Portrait', label: 'MSN Portrait', desc: '9:16 General', comp: MSN_Portrait_Composition, width: 1080, height: 1920 },
    { id: 'LoveInc_Landscape', label: 'LoveInc Landscape', desc: '16:9 General', comp: LoveInc_Landscape_Composition, width: 1920, height: 1080 },
    { id: 'TikTok_Portrait', label: 'TikTok Portrait', desc: '9:16 General', comp: TikTok_Portrait_Composition, width: 1080, height: 1920 }
];

// --- CONFIGURATION ---
const METADATA_ENDPOINT = 'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/run_refactor_pipeline';
const ADAPTATION_ENDPOINT = 'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/generate_video_adaptation';
const RENDER_ENDPOINT = 'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/route_video_render';

// Default Assets
const BRAND_ASSETS = {
    'food': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Ffood.mp4?alt=media&token=9d93d720-dd76-491a-9531-2f429dbeda0e',
    'money': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fmoney.mp4?alt=media&token=1a616261-fce9-4bba-a4d5-9bb9e3b00f57',
    'exploring': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fexploring.mp4?alt=media&token=da42411b-f64c-4cb3-9fec-b06cf2031290',
    'property': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fproperty.mp4?alt=media&token=0546d926-7714-48f9-a6f3-30f70e3cc8bb',
    'default': 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Fdefault.mp4?alt=media&token=0dd09a7e-f14d-4926-9489-734d2ea68a05'
};

const DEFAULT_MUSIC_TRACK = 'https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2FSoundtracks%2FSummer.mp3?alt=media&token=060b1785-b3ba-4732-8ae8-b7a09aeedbf1';

const DEFAULT_THEME = {
    primary: "#00d2ff",   // Cyan (Money)
    secondary: "#ffffff", // White
    accent: "#0f172a",    // Dark Slate
    fontMain: "'Outfit', sans-serif",
    fontDisplay: "'Bebas Neue', sans-serif",
};

const DEFAULT_TRANSITIONS = {
    overlap: 30,          // 1 second overlap @ 30fps
    type: "slice",        // The "Slash" effect
};

// [UPDATED] Helper: Brand Detection
const detectBrand = (dataSource) => {
    const scanString = (s) => {
        if (!s || typeof s !== 'string') return null;
        const lower = s.toLowerCase();
        if (lower.includes('money') || lower.includes('finance')) return 'money';
        if (lower.includes('food') || lower.includes('recipe') || lower.includes('cook')) return 'food';
        if (lower.includes('exploring') || lower.includes('travel') || lower.includes('destination')) return 'exploring';
        if (lower.includes('property') || lower.includes('home') || lower.includes('house') || lower.includes('interior')) return 'property';
        return null;
    };
    let detected = scanString(dataSource.brand) || scanString(dataSource.feed_name);
    if (detected) return detected;
    detected = scanString(dataSource.canonical_url) || scanString(dataSource.url);
    if (detected) return detected;
    detected = scanString(dataSource.title);
    if (detected) return detected;
    return 'money';
};

const VideoProducerLabPage = () => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { currentUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // --- STATE ---
    const [cmsId, setCmsId] = useState('');
    const [generateAudio, setGenerateAudio] = useState(true);

    const [isMetaLoading, setIsMetaLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);

    const [isRendering, setIsRendering] = useState(false);

    const [metaData, setMetaData] = useState(null);
    const [videoData, setVideoData] = useState(null);
    const [activeAudioId, setActiveAudioId] = useState(null);
    const [activeTab, setActiveTab] = useState('audition');

    const [showDisclaimer, setShowDisclaimer] = useState(true);

    // --- NEW: HUB & SPOKE STATE ARCHITECTURE ---
    const [activeStep, setActiveStep] = useState(0);
    const [requestedFormats, setRequestedFormats] = useState([
        'MSN_Landscape', 'MSN_Portrait', 'LoveInc_Landscape', 'TikTok_Portrait'
    ]); // <--- Node 1: Target Spokes
    const [formatSettings, setFormatSettings] = useState({
        MSN_Landscape: { pexelsUrl: '', pexelsCredit: null, musicUrl: '', captionCadence: 1.08, captionStyle: 'general', status: 'pending' },
        MSN_Portrait: { pexelsUrl: '', pexelsCredit: null, musicUrl: '', captionCadence: 1.08, captionStyle: 'general', status: 'pending' },
        LoveInc_Landscape: { pexelsUrl: '', pexelsCredit: null, musicUrl: '', captionCadence: 1.08, captionStyle: 'general', status: 'pending' },
        TikTok_Portrait: { pexelsUrl: '', pexelsCredit: null, musicUrl: '', captionCadence: 1.08, captionStyle: 'general', status: 'pending' }
    });


    // --- TELEMETRY ---
    const logVideoUsage = async (action, metaOverride = null) => {
        if (!currentUser) return;
        try {
            const db = getFirestore();
            const logsRef = collection(db, 'video_engine_logs');
            const activeMeta = metaOverride || metaData;
            await addDoc(logsRef, {
                uid: currentUser.uid,
                email: currentUser.email || 'unknown',
                galleryId: cmsId,
                brand: activeMeta?.brand || 'unknown',
                title: activeMeta?.title || 'unknown',
                action: action,
                audioEnabled: generateAudio,
                timestamp: serverTimestamp()
            });
        } catch (err) {
            console.warn('Telemetry logging failed:', err);
        }
    };




    // --- ACTION: FETCH METADATA ---
    const handleFetchMeta = async () => {
        if (!cmsId) return;
        setIsMetaLoading(true);
        setError(null);
        setMetaData(null);
        setVideoData(null);

        setShowDisclaimer(false); // <-- Collapse the disclaimer to save space
        // mp4Url and activeJobId resets have been removed

        const formData = new FormData();
        formData.append('cms_id', cmsId);
        formData.append('action', 'PREVIEW');
        try {
            const idToken = await currentUser.getIdToken();
            const response = await fetch(METADATA_ENDPOINT, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${idToken}` },
                body: formData
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'CMS Lookup Failed');

            const derivedBrand = detectBrand(data);
            const enhancedData = { ...data, brand: derivedBrand };
            setMetaData(enhancedData);

            // Seed the universal default B-Roll into all format settings
            const defaultAsset = BRAND_ASSETS[derivedBrand] || BRAND_ASSETS['default'];
            setFormatSettings(prev => ({
                MSN_Landscape: { ...prev.MSN_Landscape, pexelsUrl: defaultAsset, status: 'pending' },
                MSN_Portrait: { ...prev.MSN_Portrait, pexelsUrl: defaultAsset, status: 'pending' },
                LoveInc_Landscape: { ...prev.LoveInc_Landscape, pexelsUrl: defaultAsset, status: 'pending' },
                TikTok_Portrait: { ...prev.TikTok_Portrait, pexelsUrl: defaultAsset, status: 'pending' }
            }));

            setActiveStep(0); // Reset Stepper
            logVideoUsage("ARTICLE_PREVIEW", enhancedData);
        } catch (err) {
            console.error(err);
            setError(err.message);
            notifications.show({ title: 'Lookup Failed', message: err.message, color: 'red', icon: <IconAlertCircle size={16} /> });
        } finally {
            setIsMetaLoading(false);
        }
    };

    // --- ACTION: GENERATE UNIVERSAL ADAPTATION (THE HUB) ---
    const handleGenerate = async () => {
        if (!metaData) return;
        setIsGenerating(true);
        setError(null);
        setActiveStep(0); // Reset Stepper on new build

        // Payload stripped of format-specific variables
        const payload = {
            gallery_id: cmsId,
            generate_audio: generateAudio,
            requested_formats: requestedFormats // <--- Node 1: Gate payload injection
        };
        try {
            const idToken = await currentUser.getIdToken();
            const response = await fetch(ADAPTATION_ENDPOINT, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Adaptation Failed');

            setVideoData(data);
            notifications.show({ title: 'Adaptation Complete', message: 'Universal Timeline generated successfully.', color: 'green', icon: <IconCheck size={16} /> });
            logVideoUsage(generateAudio ? "RENDER_STARTED" : "DRY_RUN_EXECUTED");
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsGenerating(false);
        }
    };

    // --- STATE MUTATION HELPERS ---
    const updateFormatSetting = (formatId, key, value) => {
        setFormatSettings(prev => ({
            ...prev,
            [formatId]: { ...prev[formatId], [key]: value }
        }));
    };

    const handlePexelsOverride = (formatId, val) => {
        const match = val.match(/video\/.*?-(\d+)\/?$/) ||
            val.match(/download\/video\/(\d+)/) ||
            val.match(/^(\d+)$/);

        if (match) {
            updateFormatSetting(formatId, 'pexelsUrl', `https://www.pexels.com/download/video/${match[1]}/`);
        } else {
            updateFormatSetting(formatId, 'pexelsUrl', val);
        }
        // Force clear the credit since we cannot verify the source author
        updateFormatSetting(formatId, 'pexelsCredit', null);
    };

    const handleSkipFormat = (formatId) => {
        updateFormatSetting(formatId, 'status', 'skipped');
        setActiveStep((current) => (current < 4 ? current + 1 : current));
    };

    // --- ACTION: DISPATCH LOCALIZED JOB (THE SPOKE) ---
    const handleRenderVideo = async (formatId) => {
        if (!videoData) return;
        setIsRendering(true);
        setError(null);

        const settings = formatSettings[formatId];
        logVideoUsage("CLOUD_RENDER_INITIATED");

        try {
            const idToken = await currentUser.getIdToken();
            const payload = {
                articleId: cmsId,
                title: metaData.title,
                templateId: formatId,
                // --- TELEMETRY HOIST: Fire directly into the Orchestrator's top-level Catch-All net
                distribution_metadata: videoData?.meta?.distribution_metadata || null,
                inputProps: {
                    timeline: videoData.core_timeline ?
                        [...videoData.core_timeline, videoData.outro_matrix[formatId]] : videoData.timeline,
                    // --- PAYLOAD MERGE: Combine raw CMS source with AI-enriched metadata
                    meta: { ...metaData, ...(videoData?.meta || {}) },
                    theme: videoData.theme || DEFAULT_THEME,
                    transitions: videoData.transitions || DEFAULT_TRANSITIONS,
                    backgroundVideo: settings.pexelsUrl,
                    pexelsCredit: settings.pexelsCredit, // <--- INJECTED HERE
                    musicTrack: settings.musicUrl || DEFAULT_MUSIC_TRACK,
                    generateAudio: generateAudio,
                    captionCadence: settings.captionCadence,
                    captionStyle: settings.captionStyle
                }
            };

            const response = await fetch(RENDER_ENDPOINT, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();

            if (!response.ok) throw new Error(data.error || data.details || 'Cloud Render Dispatch Failed');

            if (data.jobId) {
                updateFormatSetting(formatId, 'status', 'dispatched');
                setActiveStep((current) => (current < 4 ? current + 1 : current));
                notifications.show({
                    title: `${FORMATS.find(f => f.id === formatId).label} Queued`,
                    message: `Job ${data.jobId.substring(0, 8)} dispatched! Check Library in ~20m.`,
                    color: 'teal',
                    icon: <IconCloudUpload size={16} />
                });
            } else {
                throw new Error("Dispatch succeeded but no Job ID was returned.");
            }

        } catch (err) {
            console.error("Render Error:", err);
            setError(err.message);
            logVideoUsage("CLOUD_RENDER_FAILED");
            notifications.show({ title: 'Render Error', message: err.message, color: 'red', icon: <IconAlertCircle size={16} /> });
        } finally {
            setIsRendering(false);
        }
    };

    const handleCopyPayload = () => {
        if (!videoData) return;
        navigator.clipboard.writeText(JSON.stringify(videoData, null, 2));
        notifications.show({ title: 'Copied!', message: 'JSON payload copied.', color: 'blue' });
    };

    const renderTimelineItem = (segment, index) => {
        const isIntroOutro = segment.type === 'intro' || segment.type === 'outro';
        const hasAudio = !!segment.props?.audioUrl;
        const segmentId = `segment-${index}`;
        return (
            <Paper key={index} p="md" shadow="md" radius="md" withBorder mb="lg" bg={isDark ? 'dark.8' : 'white'} style={{ borderColor: isIntroOutro ? theme.colors.violet[4] : theme.colors.blue[5] }}>
                <Group position="apart" align="flex-start">
                    {hasAudio ? (
                        <AudioPreview src={segment.props.audioUrl} id={segmentId} activeId={activeAudioId} onToggle={setActiveAudioId} />
                    ) : (
                        <Badge color="gray" variant="light" size="xs">Silent</Badge>
                    )}
                    <Group>
                        <Badge size="lg" color={isIntroOutro ? 'violet' : 'blue'}>
                            {segment.type === 'ranking_slide' ? `#${segment.rank}` : segment.type.toUpperCase()}
                        </Badge>
                        <Box>
                            <Text fw={700} size="sm">{segment.text || segment.name || 'Untitled Segment'}</Text>
                            <Text size="xs" c="dimmed">Duration: {segment.durationInFrames} frames</Text>
                        </Box>
                    </Group>
                </Group>
                <Divider my="sm" label="Script" labelPosition="center" />
                <Text size="md" style={{ fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>{segment.script}</Text>
                {segment.captions && (
                    <Group spacing={4} mt="sm">
                        {segment.captions.map((cap, i) => (
                            <Text key={i} size="sm" c={cap.emphasis ? 'pink.5' : 'dimmed'} fw={cap.emphasis ? 700 : 400}>{cap.text}</Text>
                        ))}
                    </Group>
                )}
            </Paper>
        );
    };

    return (
        <Box bg={isDark ? theme.colors.dark[7] : theme.colors.gray[0]} style={{ minHeight: '100vh' }}>
            {/* 1. HEADER */}
            <Paper p="md" radius={0} withBorder style={{ borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[4] }}>
                <Container size={1200}>
                    <Group justify="center" align="center" position="apart">
                        <Group>
                            <ThemeIcon size={44} radius="md" color="yellow" variant="gradient" gradient={{ from: 'yellow.9', to: 'yellow.6' }}>
                                <IconVideo size={26} />
                            </ThemeIcon>
                            <Box>
                                <Title order={4}>Video Production Lab</Title>
                                <Text size="sm" c="dimmed">Headless Scripting & Audio Engine (R&D)</Text>
                            </Box>
                        </Group>
                    </Group>
                </Container>
            </Paper>

            <Container size={1400} py="xl">
                {error && (
                    <Alert icon={<IconAlertCircle size={16} />} title="System Error" color="red" mb="lg" variant="filled">
                        {error}
                    </Alert>
                )}

                {/* 1.5. INFO / WARNING PANE */}
                <Box mb={40}>
                    <Center>
                        <Paper shadow="md" radius="lg" p="lg" withBorder w="100%" bg={isDark ? 'dark.8' : 'gray.0'} style={{ borderColor: theme.colors.orange[4], borderLeftWidth: 4 }}>

                            {/* Clickable Header */}
                            <Group
                                position="apart"
                                align="center"
                                noWrap
                                onClick={() => setShowDisclaimer(prev => !prev)}
                                style={{ cursor: 'pointer' }}
                            >
                                <Group align="center" spacing="sm">
                                    <ThemeIcon color="orange" variant="filled" size="md" ><IconInfoCircle size={22} /></ThemeIcon>
                                    <Text size="MD" fw={700} c={isDark ? 'orange.4' : 'orange.8'}>
                                        Preview Mode vs. MP4 Rendering (IMPORTANT)
                                    </Text>
                                </Group>

                                <Text size="xs" c="dimmed" style={{ textDecoration: 'underline' }}>
                                    {showDisclaimer ? '| HIDE |' : '| SHOW |'}
                                </Text>
                            </Group>

                            {/* Collapsible Content */}
                            <Collapse in={showDisclaimer}>
                                <Stack spacing={4} mt="md" pl="lg" pr="lg">

                                    <Text size="sm" c={isDark ? 'gray.4' : 'gray.9'} style={{ lineHeight: 1.5 }}>
                                        The Production Lab generates a real-time preview. If chosen, MP4 rendering will execute in the <b>background</b> and will be delivered to the <b>Video Library</b>.
                                    </Text>
                                    <Text size="sm" c={isDark ? 'gray.4' : 'gray.9'} style={{ lineHeight: 1.5 }}>
                                        1) <b>PREVIEWS</b>
                                    </Text>
                                    <List size="sm" spacing={4} withPadding>
                                        <List.Item><Text span c={isDark ? 'gray.5' : 'gray.8'} size="sm">Previews are compilations of the complex elements and assets generated. Timings are subject to browser processing capability.</Text></List.Item>
                                        <List.Item><Text span c={isDark ? 'gray.5' : 'gray.8'} size="sm">Your preview is <b>one-shot</b>. Once you leave, it is <b>lost</b>. However, you can readily 'go-again'. Each script will be <b>unique</b>.</Text></List.Item>
                                        <List.Item><Text span c={isDark ? 'gray.5' : 'gray.8'} size="sm"><b>BE AWARE:</b> If two users try to build the same Gallery ID, the last created audio files will 'win' (= visual x audio conflict.)</Text></List.Item>
                                        <List.Item><Text span c={isDark ? 'gray.5' : 'gray.8'} size="sm"><b>Quotas:</b> We've migrated to an Enterprise platform for voice gen. We hope caps aren't hit now, but FYI in case no voiceover.</Text></List.Item>
                                        <List.Item><Text span c={isDark ? 'gray.5' : 'gray.8'} size="sm"><b>iOS:</b> Apple don't allow for backing-track volume control. On an iPhone, you may struggle to hear the preview voiceover.</Text></List.Item>
                                    </List>
                                    <Text size="sm" c={isDark ? 'gray.4' : 'gray.9'} style={{ lineHeight: 1.5 }}>
                                        2) <b>MP4 RENDERING</b>
                                    </Text>
                                    <List size="sm" spacing={4} withPadding>
                                        <List.Item><Text span c={isDark ? 'gray.5' : 'gray.8'} size="sm">When you click "Send to Cloud", a job is queued.</Text></List.Item>
                                        <List.Item><Text span c={isDark ? 'gray.5' : 'gray.8'} size="sm">Visit the <b>Video Library</b> to grab your MP4. Rendering takes time, so check back in 20 minutes or so.</Text></List.Item>
                                    </List>
                                </Stack>
                            </Collapse>
                        </Paper>
                    </Center>
                </Box>

                {/* 2. THE GATE (Metadata Lookup) */}
                <Box mb={40}>
                    <Center>
                        <Paper shadow="md" radius="lg" p="xl" withBorder w="100%" bg={isDark ? 'dark.8' : 'white'}>
                            <Stack spacing="md">
                                <Group position="apart">
                                    <Text fw={700} size="lg" tt="uppercase" c="dimmed">1. Select Article</Text>
                                    {!metaData && <Badge color="gray">Waiting for Gallery</Badge>}
                                    {metaData && <Badge color="green">Ready for Production</Badge>}
                                </Group>
                                <Group align="flex-end" >
                                    <TextInput label="CMS Gallery ID" placeholder="e.g. 5278XX" value={cmsId} onChange={(e) => setCmsId(e.target.value)} style={{ flex: 1 }} rightSection={isMetaLoading ? <Loader size="xs" /> : <IconDatabase size={16} />} />
                                    <Button color="blue" onClick={handleFetchMeta} loading={isMetaLoading} disabled={!cmsId} leftSection={<IconSearch size={16} />}>Grab</Button>
                                </Group>
                                {metaData && (
                                    <Alert icon={<IconCheck size={20} />} title="Article Verified" color="green.3" variant="light" radius="md">
                                        <Group spacing="xl">
                                            <Box><Text size="sm" c="dimmed" tt="uppercase">Title</Text><Text fw={700} c="green">{metaData.title}</Text></Box>
                                            <Box><Text align="center" size="sm" c="dimmed" tt="uppercase">Brand</Text><Badge align="center" color="green" variant='outline'>{metaData.brand}</Badge></Box>
                                            <Box><Text align="center" size="sm" c="dimmed" tt="uppercase">Slides</Text><Text align="center" fw={700}>{metaData.slide_count}</Text></Box>
                                        </Group>
                                    </Alert>
                                )}
                            </Stack>
                        </Paper>
                    </Center>
                </Box>

                {/* 3. THE COCKPIT (Configuration) */}
                <Collapse in={!!metaData}>
                    <Box mb={40}>
                        <Paper shadow="sm" radius="lg" p="xl" withBorder bg={isDark ? 'dark.8' : 'white'}>
                            <Stack spacing="lg">
                                <Group position="apart">
                                    <Text fw={700} size="lg" tt="uppercase" c="dimmed">2. Production Settings</Text>
                                    <Badge color={generateAudio ? "orange" : "blue"} leftSection={<IconCurrencyDollar size={12} />}>{generateAudio ? "Higher AI Cost" : "Lower AI Cost"}</Badge>
                                </Group>
                                <Group position="apart">
                                    <Text color={generateAudio ? "orange" : "blue.4"} fw={900} size="xl" tt="uppercase" >{generateAudio ? "| Script, Visuals & Audio" : "| Script & Silent Visuals (Trial)"}</Text>
                                </Group>
                                {/* Format Settings moved to localized Finishing Suite Stepper */}
                                <Text size="sm" c="dimmed">All configurations (B-Roll, Aspect Ratios, Music) are now handled in the Finishing Suite after the build preview is requested.</Text>
                                <Divider mt="sm" variant="dashed" />
                                {/* --- NODE 1: FORMAT SELECTION UI --- */}
                                <Box mt="md">
                                    <Text fw={700} size="xl" mb="xs">Choose Target Formats</Text>
                                    <Checkbox.Group
                                        value={requestedFormats}
                                        onChange={setRequestedFormats}
                                        size="md"
                                        description="Select the variants you wish to generate tailored content for..."
                                    >
                                        <Group mt="md">
                                            {FORMATS.map((f) => (
                                                <Checkbox key={f.id} value={f.id} label={f.label} size="md" color="grape" />
                                            ))}
                                        </Group>
                                    </Checkbox.Group>
                                    {/* NEW: 0/4 Validation Warning */}
                                    {requestedFormats.length === 0 && (
                                        <Text color="red" size="xs" mt="xs" fw={500}>
                                            <IconAlertCircle size={14} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: 4 }} />
                                            You must select at least one format to proceed.
                                        </Text>
                                    )}
                                </Box>
                                <Group w="100%" justify="space-between" align="center" mt="xl">
                                    <Switch label="Generate Voiceover" description={generateAudio ? "On. Incurs additional AI charges." : "Off. Estimates timings only."} checked={generateAudio} onChange={(e) => setGenerateAudio(e.currentTarget.checked)} size="md" color="orange" thumbIcon={generateAudio ? (<IconMicrophone size="0.8rem" color={theme.colors.orange[6]} stroke={3} />) : (<IconMicrophoneOff size="0.8rem" color={theme.colors.gray[6]} stroke={3} />)} />
                                    <Button
                                        size="md"
                                        color="grape"
                                        variant="filled"
                                        onClick={handleGenerate}
                                        loading={isGenerating}
                                        disabled={requestedFormats.length === 0} // <--- NEW: The Physical Gate
                                        leftSection={<IconWand size={20} />}
                                    >{generateAudio ? "Build Video" : "Build Visuals-Only"}</Button>
                                </Group>
                            </Stack>
                        </Paper>
                    </Box>
                </Collapse>

                {/* 4. RESULTS (The Audition) */}
                {videoData && (
                    <Stack spacing="xl">
                        <Title order={3}>Producer Results </Title>
                        <Tabs value={activeTab} onChange={setActiveTab} variant="outline" radius="md">
                            <Tabs.List>
                                <Tabs.Tab value="audition" icon={<IconPlayerPlay size={16} />}>Audition (Script & Audio)</Tabs.Tab>
                                <Tabs.Tab value="json" icon={<IconCode size={16} />}>Raw Payload</Tabs.Tab>
                            </Tabs.List>
                            <Tabs.Panel value="audition" pt="xl">
                                <Box mb="xl">
                                    {/* --- NODE 2.5: DYNAMIC FORMAT FILTERING --- */}
                                    {(() => {
                                        // 1. Determine which formats survived the backend generation
                                        const activeFormats = FORMATS.filter(format => {
                                            if (videoData.core_timeline && videoData.outro_matrix) {
                                                return !!videoData.outro_matrix[format.id];
                                            }
                                            return true; // Legacy fallback
                                        });

                                        const formatCount = activeFormats.length;

                                        return (
                                            <Stepper
                                                active={activeStep}
                                                onStepClick={setActiveStep}
                                                breakpoint="sm"
                                                color="teal"
                                                // NEW: Target only the navigation header to preserve mathematical spacing
                                                styles={{
                                                    steps: {
                                                        // 4 items = 100%, 2 items = 50%. 1 item = fit to itself.
                                                        maxWidth: formatCount === 1 ? 'fit-content' : `${(formatCount / 4) * 100}%`,
                                                        margin: '0 auto', // Center the constrained block
                                                    }
                                                }}
                                            >
                                                {activeFormats.map((format) => {
                                                    const settings = formatSettings[format.id];
                                                    const isPortrait = format.id.includes('Portrait');

                                                    // Aggressively strip undefined objects to prevent durationInFrames crash
                                                    const formatTimeline = videoData.core_timeline
                                                        ? [...videoData.core_timeline, videoData.outro_matrix[format.id]].filter(Boolean)
                                                        : videoData.timeline || [];

                                                    return (
                                                        <Stepper.Step
                                                            key={format.id}
                                                            label={format.label}
                                                            description={settings.status === 'dispatched' ? 'Render Queued' : settings.status === 'skipped' ? 'Skipped' : format.desc}
                                                            color={settings.status === 'dispatched' ? 'teal' : settings.status === 'skipped' ? 'gray' : 'blue'}
                                                            icon={settings.status === 'dispatched' ? <IconCloudUpload size={18} /> : settings.status === 'skipped' ? <IconX size={18} /> : null}
                                                        >
                                                            <Grid mt="xl" gutter="xl">
                                                                <Grid.Col span={{ base: 12, lg: 8 }}>
                                                                    {/* Localized Player */}
                                                                    <div style={{
                                                                        width: '100%',
                                                                        maxWidth: isPortrait ? 400 : 850,
                                                                        aspectRatio: isPortrait ? '9/16' : '16/9',
                                                                        boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
                                                                        borderRadius: 16,
                                                                        overflow: "hidden",
                                                                        margin: '0 auto',
                                                                        display: 'flex',
                                                                        backgroundColor: '#000'
                                                                    }}>
                                                                        {format.comp ? (
                                                                            <Player
                                                                                component={format.comp}

                                                                                // INCISION: CONDITIONAL PLAYER DURATION
                                                                                // Truncates the legacy audio delay on the frontend scrubber EXCLUSIVELY for the TikTok template.
                                                                                durationInFrames={formatTimeline.reduce((acc, item) => {
                                                                                    const isTikTok = format.id === 'TikTok_Portrait';
                                                                                    const legacyDelay = isTikTok ? (item.audioDelay || 0) : 0;
                                                                                    const adjustedDuration = Math.max(1, (item.durationInFrames || 0) - legacyDelay);
                                                                                    return acc + adjustedDuration;
                                                                                }, 0) + (DEFAULT_TRANSITIONS.overlap || 30)}

                                                                                compositionWidth={format.width}
                                                                                compositionHeight={format.height}
                                                                                fps={30}
                                                                                controls
                                                                                style={{ width: "100%", height: "100%" }}
                                                                                inputProps={{
                                                                                    timeline: formatTimeline,
                                                                                    meta: metaData,
                                                                                    theme: videoData.theme || DEFAULT_THEME,
                                                                                    transitions: videoData.transitions || DEFAULT_TRANSITIONS,
                                                                                    backgroundVideo: settings.pexelsUrl,
                                                                                    pexelsCredit: settings.pexelsCredit,
                                                                                    musicTrack: settings.musicUrl || DEFAULT_MUSIC_TRACK,
                                                                                    generateAudio: generateAudio,
                                                                                    captionCadence: settings.captionCadence,
                                                                                    captionStyle: settings.captionStyle
                                                                                }}
                                                                            />
                                                                        ) : <Alert color="red">Template Offline</Alert>}
                                                                    </div>
                                                                </Grid.Col>

                                                                <Grid.Col span={{ base: 12, lg: 4 }}>
                                                                    {/* Localized Settings */}
                                                                    <Paper p="md" withBorder radius="md" bg={isDark ? 'dark.7' : 'gray.0'} style={{ borderColor: theme.colors.blue[4], borderLeftWidth: 4 }}>
                                                                        <Stack spacing="md">
                                                                            <Text fw={700} size="lg">{format.label} Configuration</Text>

                                                                            <Box>
                                                                                {(() => {
                                                                                    // 1. Data Hunting (Safely mapping the payload)
                                                                                    const pexelsSource = videoData?.meta?.pexels_candidates || metaData?.pexels_candidates || metaData?.meta?.pexels_candidates;
                                                                                    const searchPhrase = videoData?.meta?.semantic_search_phrase || metaData?.semantic_search_phrase || metaData?.meta?.semantic_search_phrase || '';
                                                                                    const candidateArray = isPortrait ? pexelsSource?.portrait : pexelsSource?.landscape;

                                                                                    // 2. Dynamic Pexels Search URL (Prefiltered by phrase and template orientation)
                                                                                    const pexelsSearchUrl = `https://www.pexels.com/search/videos/${encodeURIComponent(searchPhrase || 'background')}/?orientation=${isPortrait ? 'portrait' : 'landscape'}&min_duration=20&resolution_name=1080p`;

                                                                                    return (
                                                                                        <>
                                                                                            <Group position="apart" mb="xs" noWrap>
                                                                                                <Text fw={700} size="sm" c="dimmed" tt="uppercase">
                                                                                                    B-Roll Ideas: {searchPhrase && (

                                                                                                        <Anchor
                                                                                                            href={pexelsSearchUrl}
                                                                                                            target="_blank"
                                                                                                            rel="noopener noreferrer"
                                                                                                            c="grape"
                                                                                                            fw={800}
                                                                                                            tt="capitalize"
                                                                                                            ml={4}
                                                                                                        >
                                                                                                            "{searchPhrase}"
                                                                                                        </Anchor>
                                                                                                    )}
                                                                                                </Text>
                                                                                            </Group>

                                                                                            {/* The 6-Grid Pexels Array */}
                                                                                            {candidateArray && candidateArray.length > 0 && (
                                                                                                <SimpleGrid cols={3} mb="sm" spacing="xs">
                                                                                                    {candidateArray.slice(0, 6).map((candidate) => (
                                                                                                        <Card
                                                                                                            key={candidate.id}
                                                                                                            p={0}
                                                                                                            radius="sm"
                                                                                                            withBorder
                                                                                                            style={{
                                                                                                                cursor: 'pointer',
                                                                                                                borderColor: settings.pexelsUrl.includes(candidate.id.toString())
                                                                                                                    ? theme.colors.blue[5]
                                                                                                                    : 'transparent',
                                                                                                                borderWidth: 2
                                                                                                            }}
                                                                                                            onClick={() => {
                                                                                                                updateFormatSetting(format.id, 'pexelsUrl', candidate.preview_url);
                                                                                                                updateFormatSetting(format.id, 'pexelsCredit', candidate.photographer);
                                                                                                            }}
                                                                                                        >
                                                                                                            <Image src={candidate.thumbnail} height={60} fit="cover" />
                                                                                                        </Card>
                                                                                                    ))}
                                                                                                </SimpleGrid>
                                                                                            )}

                                                                                            {/* Manual Override & External Search Link */}
                                                                                            <Group spacing="xs" wrap="nowrap">
                                                                                                <TextInput
                                                                                                    placeholder="Manual ID or Pexels URL"
                                                                                                    value={settings.pexelsUrl}
                                                                                                    onChange={(e) => handlePexelsOverride(format.id, e.target.value)}
                                                                                                    icon={<IconVideo size={16} />}
                                                                                                    size="xs"
                                                                                                    style={{ flex: 1 }}
                                                                                                />
                                                                                                <Tooltip
                                                                                                    label="To choose a custom video: CLICK HERE to search Pexels.com. On the video page, right-click the video player and select 'Copy Video link', then paste it here."
                                                                                                    withArrow
                                                                                                    multiline
                                                                                                    w={250}
                                                                                                    color="dark"
                                                                                                >
                                                                                                    <ActionIcon
                                                                                                        component="a"
                                                                                                        href={pexelsSearchUrl}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                        color="grape"
                                                                                                        variant="light"
                                                                                                        size="md" // Aligns vertically with the size="xs" TextInput
                                                                                                    >
                                                                                                        <IconExternalLink size={16} />
                                                                                                    </ActionIcon>
                                                                                                </Tooltip>
                                                                                            </Group>
                                                                                        </>
                                                                                    );
                                                                                })()}
                                                                            </Box>

                                                                            <TextInput
                                                                                label={<Text size="xs" c="dimmed" fw={700} tt="uppercase">Music Track Override</Text>}
                                                                                placeholder="https://..."
                                                                                value={settings.musicUrl}
                                                                                onChange={(e) => updateFormatSetting(format.id, 'musicUrl', e.target.value)}
                                                                                icon={<IconMusic size={16} />}
                                                                                size="xs"
                                                                            />

                                                                            <Box>
                                                                                <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={4}>Caption Cadence</Text>
                                                                                <Slider value={settings.captionCadence} onChange={(val) => updateFormatSetting(format.id, 'captionCadence', val)} min={0.5} max={1.5} step={0.01} color="blue" size="sm" />
                                                                            </Box>

                                                                            <Box>
                                                                                <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={4}>Caption Style</Text>
                                                                                <SegmentedControl
                                                                                    size="xs"
                                                                                    value={settings.captionStyle}
                                                                                    onChange={(val) => updateFormatSetting(format.id, 'captionStyle', val)}
                                                                                    data={[
                                                                                        { label: 'Social', value: 'social' },
                                                                                        { label: 'Broadcast', value: 'broadcast' },
                                                                                        { label: 'General', value: 'general' }
                                                                                    ]}
                                                                                    fullWidth
                                                                                />
                                                                            </Box>

                                                                            <Divider my="xs" />

                                                                            {/* Dispatch Controls */}
                                                                            <Group grow>
                                                                                <Button variant="light" color="gray" leftSection={<IconPlayerSkipForward size={16} />} onClick={() => handleSkipFormat(format.id)}>Skip</Button>
                                                                                <Button color="teal" leftSection={<IconCloudUpload size={16} />} onClick={() => handleRenderVideo(format.id)} loading={isRendering}>Queue Render</Button>
                                                                            </Group>
                                                                        </Stack>
                                                                    </Paper>
                                                                </Grid.Col>
                                                            </Grid>
                                                        </Stepper.Step>
                                                    );
                                                })}

                                                <Stepper.Completed>
                                                    <Paper shadow="sm" radius="md" p="xl" withBorder bg={isDark ? 'dark.8' : 'white'} style={{ borderColor: theme.colors.teal[5], borderWidth: 2 }}>
                                                        <Stack align="center" spacing="md">
                                                            <ThemeIcon size={48} radius="xl" color="teal" variant="light">
                                                                <IconCheck size={32} />
                                                            </ThemeIcon>
                                                            <Title order={4} c="teal">Finishing Suite Complete</Title>
                                                            <Text size="sm" c="dimmed" align="center" maw={600}>
                                                                All targeted formats have been processed. You can safely leave this page and check the <b>Video Library</b> for your final rendered MP4s.
                                                            </Text>
                                                            <Button variant="outline" color="gray" onClick={() => setActiveStep(0)}>Review Formats Again</Button>
                                                        </Stack>
                                                    </Paper>
                                                </Stepper.Completed>
                                            </Stepper>
                                        );
                                    })()}
                                </Box>

                                {/* REMOVED: Old Cloud Render UI was deleted from here */}

                                <Divider label="Universal Script & Metadata" labelPosition="center" mb="lg" />

                                <Divider label="Core Timeline (Universal)" labelPosition="center" mb="lg" />
                                {videoData.core_timeline?.map((segment, idx) => renderTimelineItem(segment, idx))}

                                {videoData.outro_matrix && (
                                    <>
                                        <Divider label="Format-Specific Outros (Spokes)" labelPosition="center" mb="lg" mt="xl" />
                                        {Object.entries(videoData.outro_matrix).map(([formatId, outroSegment], idx) => (
                                            <Box key={`outro-${formatId}`} style={{ position: 'relative' }}>
                                                <Badge color="violet" variant="filled" style={{ position: 'absolute', top: -12, left: 16, zIndex: 1 }}>{formatId}</Badge>
                                                {renderTimelineItem(outroSegment, `outro-${idx}`)}
                                            </Box>
                                        ))}
                                    </>
                                )}

                                {/* Legacy Fallback */}
                                {!videoData.core_timeline && videoData.timeline?.map((segment, idx) => renderTimelineItem(segment, idx))}
                            </Tabs.Panel>
                            <Tabs.Panel value="json" pt="xl">
                                <Group position="apart" mb="sm">
                                    <Text size="sm" c="dimmed">Raw JSON used for R&D rendering.</Text>
                                    <Button variant="light" color="blue" size="xs" leftSection={<IconCopy size={14} />} onClick={handleCopyPayload}>Copy Payload</Button>
                                </Group>
                                <Code block>{JSON.stringify(videoData, null, 2)}</Code>
                            </Tabs.Panel>
                        </Tabs>
                    </Stack>
                )}
            </Container>
        </Box>
    );
};

export default VideoProducerLabPage;